/* eslint-disable jsx-a11y/heading-has-content */
import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import { LayoutGroup, motion } from 'framer-motion'
import React, { forwardRef, useId } from 'react'
import { TouchTarget } from './button'
import { Link } from './link'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid'

export function Sidebar({ className, isCollapsed, onCollapseChange, ...props }) {
  return (
    <nav 
      {...props} 
      className={clsx(
        className, 
        'flex h-full min-h-0 flex-col',
        isCollapsed ? 'w-16' : 'w-64'
      )} 
    >
      {isCollapsed && (
        <button
          onClick={onCollapseChange}
          className="lg:hidden w-full p-2 hover:bg-zinc-200 dark:hover:bg-zinc-800 transition-colors border-b border-zinc-200 dark:border-zinc-700"
          aria-label="Expand sidebar"
        >
          <ChevronRightIcon className="w-4 h-4 text-zinc-500 mx-auto" />
        </button>
      )}
      {props.children}
    </nav>
  )
}

export function SidebarHeader({ className, isCollapsed, onCollapseChange, children, ...props }) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'relative flex flex-col border-b border-zinc-950/5 p-4 dark:border-white/5 [&>[data-slot=section]+[data-slot=section]]:mt-2.5',
        isCollapsed ? 'items-center' : ''
      )}
    >
      {children}
      {/* Handle de colapso montado sobre el borde derecho, fuera del flujo:
          así el logo puede ocupar todo el ancho del header sin encimarse. */}
      <button
        onClick={onCollapseChange}
        className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 h-6 w-6 items-center justify-center rounded-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 shadow-sm hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {isCollapsed ? (
          <ChevronRightIcon className="w-4 h-4 text-zinc-500" />
        ) : (
          <ChevronLeftIcon className="w-4 h-4 text-zinc-500" />
        )}
      </button>
    </div>
  )
}

export function SidebarBody({ className, isCollapsed, ...props }) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex flex-1 flex-col overflow-y-auto p-4 [&>[data-slot=section]+[data-slot=section]]:mt-8',
        isCollapsed ? 'items-center' : ''
      )}
    />
  )
}

export function SidebarFooter({ className, isCollapsed, ...props }) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex flex-col border-t border-zinc-950/5 p-4 dark:border-white/5 [&>[data-slot=section]+[data-slot=section]]:mt-2.5',
        isCollapsed ? 'items-center' : ''
      )}
    />
  )
}

export function SidebarSection({ className, isCollapsed, ...props }) {
  let id = useId()

  return (
    <LayoutGroup id={id}>
      <div 
        {...props} 
        data-slot="section" 
        className={clsx(
          className, 
          'flex flex-col gap-0.5',
          isCollapsed ? 'items-center' : ''
        )} 
      />
    </LayoutGroup>
  )
}

export function SidebarDivider({ className, isCollapsed, ...props }) {
  return (
    <hr 
      {...props} 
      className={clsx(
        className, 
        'my-4 border-t border-zinc-950/5 lg:-mx-4 dark:border-white/5',
        isCollapsed ? 'w-8' : ''
      )} 
    />
  )
}

export function SidebarSpacer({ className, isCollapsed, ...props }) {
  return (
    <div 
      aria-hidden="true" 
      {...props} 
      className={clsx(className, 'mt-8 flex-1')} 
    />
  )
}

export function SidebarHeading({ className, isCollapsed, ...props }) {
  return (
    <h3 
      {...props} 
      className={clsx(
        className, 
        'mb-1 px-2 text-xs/6 font-medium text-zinc-500 dark:text-zinc-400',
        isCollapsed ? 'hidden' : ''
      )} 
    />
  )
}

export const SidebarItem = forwardRef(function SidebarItem(
  { current, className, children, isCollapsed, ...props },
  ref
) {
  let classes = clsx(
    // Base
    'flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left text-base/6 font-medium text-zinc-950 sm:py-2 sm:text-sm/5',
    // Leading icon/icon-only
    'data-[slot=icon]:*:size-6 data-[slot=icon]:*:shrink-0 data-[slot=icon]:*:fill-zinc-500 sm:data-[slot=icon]:*:size-5',
    // Trailing icon (down chevron or similar)
    'data-[slot=icon]:last:*:ml-auto data-[slot=icon]:last:*:size-5 sm:data-[slot=icon]:last:*:size-4',
    // Avatar
    'data-[slot=avatar]:*:-m-0.5 data-[slot=avatar]:*:size-7 data-[slot=avatar]:*:[--ring-opacity:10%] sm:data-[slot=avatar]:*:size-6',
    // Hover
    'data-[hover]:bg-[#1b4d2e]/10 data-[slot=icon]:*:data-[hover]:fill-[#1b4d2e]',
    // Active
    'data-[active]:bg-[#1b4d2e]/15 data-[slot=icon]:*:data-[active]:fill-[#1b4d2e]',
    // Current — green bubble with white text
    'data-[current]:bg-[#1b4d2e] data-[current]:text-white data-[slot=icon]:*:data-[current]:fill-white',
    // Current + hover/active — keep solid green
    'data-[current]:data-[hover]:bg-[#245a38] data-[current]:data-[active]:bg-[#245a38]',
    'data-[current]:data-[hover]:text-white data-[slot=icon]:*:data-[current]:data-[hover]:fill-white',
    // Dark mode
    'dark:text-white dark:data-[slot=icon]:*:fill-zinc-400',
    'dark:data-[hover]:bg-[#1b4d2e]/20 dark:data-[slot=icon]:*:data-[hover]:fill-[#1b4d2e]',
    'dark:data-[active]:bg-[#1b4d2e]/25 dark:data-[slot=icon]:*:data-[active]:fill-[#1b4d2e]',
    'dark:data-[current]:bg-[#1b4d2e] dark:data-[current]:text-white dark:data-[slot=icon]:*:data-[current]:fill-white',
    'dark:data-[current]:data-[hover]:bg-[#245a38] dark:data-[current]:data-[active]:bg-[#245a38]',
    // Collapsed state
    isCollapsed ? 'justify-center' : ''
  )

  return (
    <span className={clsx(className, 'relative')}>
      {current && (
        <motion.span
          layoutId="current-indicator"
          className={clsx(
            "absolute inset-y-2 w-0.5 rounded-full bg-[#1b4d2e]",
            isCollapsed ? "left-0" : "-left-4"
          )}
        />
      )}
      {'href' in props ? (
        <Headless.CloseButton
          as={Link}
          {...props}
          className={classes}
          data-current={current ? 'true' : undefined}
          ref={ref}
        >
          <TouchTarget>{children}</TouchTarget>
        </Headless.CloseButton>
      ) : (
        <Headless.Button
          {...props}
          className={clsx('cursor-default', classes)}
          data-current={current ? 'true' : undefined}
          ref={ref}
        >
          <TouchTarget>{children}</TouchTarget>
        </Headless.Button>
      )}
    </span>
  )
})

export function SidebarLabel({ className, isCollapsed, ...props }) {
  return (
    <span 
      {...props} 
      className={clsx(
        className, 
        'truncate',
        isCollapsed ? 'hidden' : ''
      )} 
    />
  )
}
